import { SourceName } from "../sources/types";
// Global types for the app
import { DestinationValidity } from "@/destinations/validation";
import { DestinationKey } from "../destinations/types";
import { DestinationCategory } from "../destinations/v2/DestinationCategory";
import { EventSource, EventType } from "../eventTypes";
import { AdminAccountMetadataResponse } from "../admin/queries/useAdminAccountMetadata";
import { ProjectID } from "../reducers/environments";

export type EnvironmentID = string;

type EnabledDestinationInfo = Record<string, boolean>;

export type EventDefinitionFilter = {
  readonly negate?: boolean;
  readonly op: "=" | "contains" | "css_matches" | "wildcard_matches" | "any_of";
  readonly prop: string;
  readonly type: string;
  readonly val: string;
  readonly nw_path?: string;
};

export type EventSnapshotType = "js_code" | "css_selector" | "network";

export type EventSnapshot = {
  readonly name: string;
  readonly value: string;
  readonly type: EventSnapshotType;
};

type EventDefinitionFilters = {
  readonly type: string;
  readonly filters: EventDefinitionFilter[];
};

export type LoadedEventDefinition = {
  enabled_dests: EnabledDestinationInfo;
  enabled_client_destinations: EnabledDestinationInfo;
  enabled_server_destinations: EnabledDestinationInfo;
  deleted_at?: number;
  updated_at?: number;
  created_at?: number;
  created_by?: string;
  last_updated_by?: string;
  id: string;
  name: string;
  segment_compatible: boolean;
  source: EventSource;
  // TODO: every event def should have a type, but there are many in the DB that
  // have type = null. We should backfill every event def type and make sure all
  // new event defs have a type, and then this doesn't need to be nullable.
  type: EventType | null;
  filter: EventDefinitionFilters; // TODO
  is_hidden: boolean;
  description: string;
  tags: Record<string, true>;
  snapshots: EventSnapshot[];
  sql_query?: string;
};

// An event definition without any context about which environment it might be referenced within and without data about
// that environment's destinations.
export type BareEventDefinition = Omit<
  LoadedEventDefinition,
  | "enabled_dests"
  | "enabled_server_destinations"
  | "enabled_client_destinations"
>;

export type SyncStatus = {
  proj_id: ProjectID;
  env_id: EnvironmentID;
  event_def_id: string;
  last_completed_at?: number;
  last_num_events: number;
  last_error_message?: string;
  lock_value?: string;
};

export type DestinationConnectionMode = "client" | "server";

export enum SensitivityLevel {
  NoSensitivitySet = "NO_SENSITIVITY_SET",
  NotSensitive = "NOT_SENSITIVE",
  NotSensitiveIfHashed = "NOT_SENSITIVE_IF_HASHED",
}

export type PropertySensitivityLevels = Record<string, SensitivityLevel>;

export type ConfiguredDestinationValidity = {
  validity: DestinationValidity;
  warningMessage?: string;
};

export type LoadedDestinationInfo = {
  destinationCategories: DestinationCategory[];
  enabled: boolean;
  env_id: string;
  id: string;
  type_slug: string;
  is_valid: boolean;
  is_valid_client_side: boolean;
  is_valid_server_side: boolean;
  fivetran_group_id: string | null;
  name: DestinationKey;
  instance_name: string;
  custom_label: string;
  settings: Record<string, any>;
  mapping?: Array<any>;
  has_baa: boolean;
  connection_mode: DestinationConnectionMode;
  function_id?: string;
  event_property_sensitivity_levels?: PropertySensitivityLevels;
  user_property_sensitivity_levels?: PropertySensitivityLevels;
  group_property_sensitivity_levels?: PropertySensitivityLevels;
  connected_audiences_count: number;
  resolvedValidity?: ConfiguredDestinationValidity;
};

export type ConsentManagementType = "freshpaint" | "osano" | "onetrust";

export type ConsentManagementActiveVersion = {
  id: string;
  envID: string;
  type: ConsentManagementType;
  revision?: number;
  publishedAt?: string;
  consentModel: "opt-in" | "opt-out";
  categoryMapping: Record<string, string>;
  consentMapping: Record<string, string>;
  freshpaintConfig?: any;
  createdAt: string;
  updatedAt: string;
};

export type LoadedConsentManagementInfo = {
  id: string;
  envID: string;
  type: ConsentManagementType;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
  activePublishedVersion: ConsentManagementActiveVersion | null;
};

export type LoadedSourceInfo = {
  env_id: string;
  name: SourceName;
  settings: Record<string, any>;
  last_sync?: LastSyncInfo;
  sync_in_progress: boolean;
  enabled: boolean;
  schedule: string;
  num_configured_tables: number;
};

type LastSyncInfo = {
  last_sync_time: number;
  last_sync_failed: boolean;
  failure_reason?: string;
};

export type AdminGetDestinationResponse = {
  account: AdminAccountMetadataResponse;
  destination: LoadedDestinationInfo;
};
